import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AudioManager = ({ lineData, audioFile, onAudioUpload, onAudioDelete, isUploading, recordedBy, onRecordedByChange, availableUsers }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter to only show voice actors
  const voiceActorUsers = availableUsers?.filter(user => user?.isVoiceActor === true) || [];

  // Get selected user display name
  const selectedUser = voiceActorUsers?.find(user => user?.id === recordedBy);
  const selectedUserDisplay = selectedUser ? `${selectedUser?.name} (${selectedUser?.email})` : '';
  const selectedUserName = selectedUser?.name || 'Not assigned';

  // Initialize and update search query with selected user
  useEffect(() => {
    setSearchQuery(selectedUserDisplay);
  }, [selectedUserDisplay]);

  // Filter users based on search query
  const filteredUsers = voiceActorUsers?.filter(user => {
    const query = searchQuery?.toLowerCase() || '';
    const name = user?.name?.toLowerCase() || '';
    const email = user?.email?.toLowerCase() || '';
    return name?.includes(query) || email?.includes(query);
  }) || [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = (e) => {
    setSearchQuery(e?.target?.value);
    setShowDropdown(true);
  };

  const handleUserSelect = (user) => {
    setSearchQuery(`${user?.name} (${user?.email})`);
    onRecordedByChange?.(user?.id);
    setShowDropdown(false);
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleDropdownToggle = (e) => {
    e?.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio?.currentTime || 0);
    const updateDuration = () => setDuration(audio?.duration || 0);

    audio?.addEventListener('timeupdate', updateTime);
    audio?.addEventListener('loadedmetadata', updateDuration);

    return () => {
      audio?.removeEventListener('timeupdate', updateTime);
      audio?.removeEventListener('loadedmetadata', updateDuration);
    };
  }, [audioFile]);

  const handleFileSelect = async (event) => {
    const file = event?.target?.files?.[0];
    if (file && file?.type === 'audio/mpeg') {
      // Call parent handler which will trigger actual upload
      await onAudioUpload(file);
    } else {
      alert('Please select a valid MP3 file');
    }
  };

  const handlePlayPause = () => {
    if (audioRef?.current) {
      if (isPlaying) {
        audioRef?.current?.pause();
      } else {
        audioRef?.current?.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const audio = audioRef?.current;
    if (!audio) return;
    
    const rect = e?.currentTarget?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const percentage = x / rect?.width;
    const newTime = percentage * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="p-6 bg-background border-t border-border">
      <div className="flex items-start gap-3 mb-4">
        <Icon name="Music" size={20} className="text-primary mt-1" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground mb-1">
            Audio Pronunciation
          </h3>
          <p className="text-xs text-muted-foreground">
            Upload MP3 file for line pronunciation guidance
          </p>
        </div>
      </div>
      {/* User Selection Dropdown */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <label className="text-sm font-medium text-foreground">
            Recorded By:
          </label>
          <span className="text-sm font-semibold text-primary">
            {selectedUserName}
          </span>
        </div>
        <div className="relative">
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            placeholder="Search voice actors..."
            className={`w-full pr-10 ${showDropdown ? 'border-primary' : ''}`}
          />
          <button
            onClick={handleDropdownToggle}
            className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
          >
            <Icon name="ChevronDown" size={16} />
          </button>
          {showDropdown && (
            <div ref={dropdownRef} className="absolute z-10 mt-1 w-full bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              <div className="p-2">
                {filteredUsers?.length > 0 ? (
                  filteredUsers?.map((user) => (
                    <div
                      key={user?.id}
                      className={`flex items-center justify-between p-2 cursor-pointer rounded hover:bg-muted transition-all ${
                        selectedUser?.id === user?.id ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => handleUserSelect(user)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <Icon name="User" size={16} className="text-muted-foreground" />
                        </div>
                        <span className="text-sm font-medium text-foreground">{user?.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-sm text-muted-foreground p-2">No users found</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {audioFile ? (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                <Icon name="CheckCircle" size={24} className="text-success" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{audioFile?.name || 'Audio File'}</p>
                <p className="text-xs text-muted-foreground">
                  {audioFile?.size ? `${(audioFile?.size / 1024)?.toFixed(2)} KB` : formatTime(duration)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onAudioDelete}
              iconName="Trash2"
              iconSize={16}
            >
              Remove
            </Button>
          </div>

          {/* Inline Audio Player */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePlayPause}
                iconName={isPlaying ? 'Pause' : 'Play'}
                iconPosition="left"
              >
                {isPlaying ? 'Pause' : 'Play'}
              </Button>
              <span className="text-xs text-muted-foreground font-mono">
                {formatTime(currentTime)}
              </span>
              <div 
                className="flex-1 h-2 bg-muted rounded-full overflow-hidden cursor-pointer hover:h-3 transition-all"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-primary transition-all duration-150" 
                  style={{ width: `${progressPercentage}%` }} 
                />
              </div>
              <span className="text-xs text-muted-foreground font-mono">
                {formatTime(duration)}
              </span>
            </div>

            {/* Waveform-style visualization */}
            <div className="flex items-center gap-0.5 h-8">
              {Array.from({ length: 40 })?.map((_, i) => {
                const barProgress = (i / 40) * 100;
                const isActive = barProgress <= progressPercentage;
                const height = Math.random() * 60 + 40;
                return (
                  <div
                    key={i}
                    className={`flex-1 rounded-sm transition-all duration-150 ${
                      isActive ? 'bg-primary' : 'bg-muted'
                    }`}
                    style={{ height: `${height}%` }}
                  />
                );
              })}
            </div>
          </div>

          <audio
            ref={audioRef}
            src={audioFile?.url || (audioFile instanceof File ? URL.createObjectURL(audioFile) : null)}
            onEnded={handleAudioEnded}
            className="hidden"
          />
        </div>
      ) : (
        <div className="bg-card border-2 border-dashed border-border rounded-lg p-8">
          {isUploading ? (
            <div className="text-center">
              <Icon name="Upload" size={32} className="text-primary mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground mb-2">Uploading...</p>
              <div className="w-full max-w-xs mx-auto h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">{uploadProgress}%</p>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Icon name="Upload" size={32} className="text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Upload Audio File
                  </p>
                  <p className="text-xs text-muted-foreground">
                    MP3 format only, max 10MB
                  </p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef?.current?.click()}
                  iconName="Upload"
                  iconPosition="left"
                >
                  Choose File
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AudioManager;